/**
 * lib/firebase.ts
 * ------------------------------------------------------------------
 * Firebase initialisation + typed callables. Web config comes from
 * Vite env vars (VITE_*). Functions are region-pinned to match the
 * Cloud Functions deployment region.
 * ------------------------------------------------------------------
 */
import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, type Auth } from 'firebase/auth';
import { getFirestore, type Firestore } from 'firebase/firestore';
import { getStorage, type FirebaseStorage } from 'firebase/storage';
import { getFunctions, httpsCallable, type Functions } from 'firebase/functions';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Keep this region in sync with functions/index.js (setGlobalOptions).
export const FUNCTIONS_REGION = 'europe-west1';

// Named Firestore database (the original "(default)" was deleted; its ID is
// stuck-reserved, so the app runs on this named DB). Keep in sync with
// functions/index.js, scripts/setup-operator.mjs, and firebase.json.
export const FIRESTORE_DATABASE_ID = 'grace-haven';

export const app: FirebaseApp = initializeApp(firebaseConfig);
export const auth: Auth = getAuth(app);
export const db: Firestore = getFirestore(app, FIRESTORE_DATABASE_ID);
export const storage: FirebaseStorage = getStorage(app);
export const functions: Functions = getFunctions(app, FUNCTIONS_REGION);

// ── Typed callables ──────────────────────────────────────────────

export interface BookingSubmission {
  hp?: string; // honeypot — must stay empty
  fullName: string;
  email: string;
  phone?: string;
  date?: string;
  time?: string;
  selectedServices: { id: string; name: string; durationMin: number; price: number }[];
  totalPrice: number;
  totalMin: number;
  specialRequests?: string;
}

export const submitBooking = httpsCallable<BookingSubmission, { ok: boolean; id?: string }>(
  functions,
  'submitBooking',
);

export interface SendEmailPayload {
  to: string;
  cc?: string;
  bcc?: string;
  subject: string;
  body: string;
  html?: string;
  attachments?: { filename: string; contentBase64: string; contentType: string }[];
  relatedEntityType?: string;
  relatedEntityId?: string;
}

export const sendGraceHavenEmail = httpsCallable<SendEmailPayload, { ok: boolean }>(
  functions,
  'sendGraceHavenEmail',
);

export interface GiftCardSubmission {
  hp?: string;
  amount: number;
  recipientName: string;
  recipientEmail?: string;
  fromName?: string;
  buyerEmail?: string;
  buyerPhone?: string;
  message?: string;
}
export const submitGiftCard = httpsCallable<GiftCardSubmission, { ok: boolean; id?: string }>(
  functions,
  'submitGiftCard',
);

export interface StaySubmission {
  hp?: string;
  fullName: string;
  email: string;
  phone?: string;
  rooms?: { roomId?: string; roomName: string; price: number }[];
  roomId?: string;
  roomName?: string;
  checkIn: string;
  checkOut: string;
  guests?: number;
  nights?: number;
  totalPrice?: number;
  notes?: string;
}
export const submitStayRequest = httpsCallable<StaySubmission, { ok: boolean; id?: string }>(
  functions,
  'submitStayRequest',
);

export interface CarHireSubmission {
  hp?: string;
  fullName: string;
  email: string;
  phone?: string;
  vehicleId?: string;
  vehicleName?: string;
  pickup: string;
  dropoff: string;
  days?: number;
  pricePerDay?: number;
  totalPrice?: number;
  driverAge?: string;
  notes?: string;
}
export const submitCarHireRequest = httpsCallable<CarHireSubmission, { ok: boolean; id?: string }>(
  functions,
  'submitCarHireRequest',
);

export type OperatorRole = 'owner' | 'manager' | 'staff';

export interface CreateOperatorPayload { email: string; name?: string; role: OperatorRole; }
export const createOperator = httpsCallable<
  CreateOperatorPayload,
  { ok: boolean; created: boolean; emailed: boolean; tempPassword?: string | null }
>(functions, 'createOperator');

export const resetOperatorPassword = httpsCallable<{ email: string }, { ok: boolean; emailed: boolean; link?: string }>(
  functions,
  'resetOperatorPassword',
);

export const deleteOperator = httpsCallable<{ email: string }, { ok: boolean }>(functions, 'deleteOperator');
